import React, { useMemo } from 'react';
import { Box, Flex, TextV2 } from '@withjoy/joykit';
import type { PriceValuePropAsset } from '../../../propValues.types';
import { useIsMobileScreen } from '@shared/utils/media/useMediaScreens';
import { galleryDesktopAspect, galleryMobileAspect, priceValuePropBackground } from '@apps/card/components/CardsConfig';
import { useStationeryTemplateProductPrices } from '../hooks/useStationeryTemplateProductPrices';
import { useGalleryConciergeProducts } from '../../../useGalleryConciergeProducts';
import { useCardDiscountContext } from '@apps/card/contexts/useCardDiscountContext';
import { useCardCategoryContext } from '@apps/card/components/CardCategoryProvider';
import { useStationeryTemplateCategoryTranslations } from '@apps/card/hooks/useStationeryTemplateCategoryTranslations';
import { getConciergeSuiteUnitPrice, isConciergeServiceCategory } from '@apps/card/concierge/conciergeConfig';
import { useFormatPriceString } from '@apps/card/hooks/useFormattedPriceString';
import type { ConciergeServiceCategory } from '@apps/card/concierge/conciergeConfig';
import { useTranslation } from '@shared/core';
import { isBasicDigitalV2 } from '@apps/card/components/ProductUIConfig/isBasicDigitalV2';
import { isDigitalProduct } from '@apps/card/components/ProductUIConfig/isDigitalProduct';

type PriceValuePropCardProps = {
  data: PriceValuePropAsset & { type: 'priceValueProp' };
};

const PriceRow = ({ label, price, strikethroughPrice }: { label: string; price: string; strikethroughPrice?: string }) => (
  <Flex
    flexDirection="row"
    alignItems="flex-end"
    gap={{ _: '4px', md: '6px' }}
  >
    <Box>
      <TextV2
        as="span"
        fontSize={{ _: '11px', md: '15px' }}
        fontWeight="regular"
        lineHeight="1.4"
        color="white"
      >
        {label}
        {strikethroughPrice !== undefined ? ' ' : ''}
      </TextV2>
      {strikethroughPrice !== undefined && (
        <TextV2
          as="span"
          fontSize={{ _: '11px', md: '15px' }}
          fontWeight="regular"
          lineHeight="1.4"
          color="white"
          textDecoration="line-through"
        >
          {strikethroughPrice}
        </TextV2>
      )}
    </Box>
    <TextV2
      typographyVariant="hed2"
      fontSize={{ _: '13px', md: '17px' }}
      color="white"
    >
      {price}
      {strikethroughPrice !== undefined && '*'}
    </TextV2>
  </Flex>
);

export const PriceValuePropCard = ({ data }: PriceValuePropCardProps) => {
  const isMobile = useIsMobileScreen();
  const { tq } = useTranslation('stationery');

  const productPrices = useStationeryTemplateProductPrices(data.representativeTemplate);
  // The free Basic Digital V2 row is hidden: it would sit beside premium digital under the
  // same "Digital" label, and only appears when the representative template happens to carry it.
  // Digital before Paper — hook returns a fresh array each render so no memo needed
  const sortedProductPrices = productPrices
    .filter(p => !isBasicDigitalV2({ id: p.productId, productType: p.productType }))
    .sort((a, b) => {
      const isDigitalA = isDigitalProduct(a);
      const isDigitalB = isDigitalProduct(b);
      return isDigitalA === isDigitalB ? 0 : isDigitalA ? -1 : 1;
    });

  const { currentCategory } = useCardCategoryContext();
  // Headline noun tracks the category being browsed. Of the `stationeryTemplateCategory`
  // key sets this is the only one that is plural for every category *and* has a non-empty
  // `default` — `stationeryTemplateCategoryToTile` is singular for saveTheDate/invitation,
  // and `dashboardCategoryNav` defaults to an empty string.
  const getCategoryNoun = useStationeryTemplateCategoryTranslations('designsGalleryPdpBreadcrumbCategory');
  const categoryNoun = getCategoryNoun(currentCategory) || tq`Cards`;
  const { conciergeProducts } = useGalleryConciergeProducts();
  const cardDiscountContext = useCardDiscountContext();
  const formatPrice = useFormatPriceString();

  const conciergeRow = useMemo(() => {
    if (!isConciergeServiceCategory(currentCategory)) return null;
    const categoryProducts = conciergeProducts[currentCategory as ConciergeServiceCategory] ?? [];
    if (!categoryProducts.length) return null;

    // USD per-card prices at 100 cards. `useFormatPriceString` converts them with the picker's currency,
    // as it does for every other price on this card.
    const cheapestUnitPrice = Math.min(...categoryProducts.map(o => getConciergeSuiteUnitPrice(currentCategory as ConciergeServiceCategory, o.identifier, 100)));
    const discountResult = cardDiscountContext!.paperlust.getPrice(cheapestUnitPrice);

    const sale = 'currentSale' in discountResult ? discountResult.currentSale : undefined;
    const discountedUnitPrice = sale ? Math.round(cheapestUnitPrice * (1 - sale.discountPercentage)) : null;

    return {
      basePrice: formatPrice(cheapestUnitPrice),
      discountedPrice: discountedUnitPrice !== null ? formatPrice(discountedUnitPrice) : undefined,
      preDiscountPrice: discountedUnitPrice !== null ? formatPrice(cheapestUnitPrice) : undefined,
      promoCode: sale?.code,
    };
  }, [currentCategory, conciergeProducts, cardDiscountContext, formatPrice]);

  const promoCode = sortedProductPrices.find(p => p.discount)?.discount?.promoCode ?? conciergeRow?.promoCode;
  const hasAnyDiscount = sortedProductPrices.some(p => !!p.discount) || !!conciergeRow?.discountedPrice;

  return (
    <Flex
      gridArea="auto"
      aspectRatio={isMobile ? galleryMobileAspect : galleryDesktopAspect}
      flexDirection="column"
      background={priceValuePropBackground}
      width="100%"
      height="100%"
      overflow="hidden"
      paddingTop={{ _: 8, md: '50px' }}
      paddingX={{ _: 5, md: 7 }}
    >
      <TextV2
        fontSize={{ _: '30px', md: '44px' }}
        fontWeight="semibold"
        lineHeight="1.2"
        letterSpacing="-0.015em"
        color="white"
      >
        {tq`${categoryNoun} for every budget.`}
      </TextV2>

      <Flex
        flexDirection="column"
        gap="4px"
        marginTop={{ _: 7, md: '36px' }}
      >
        {sortedProductPrices.map(product => {
          const hasDiscount = !!product.discount && product.preDiscountedPrice !== product.basePrice;
          return (
            <PriceRow
              key={product.productId}
              label={product.name}
              price={product.basePrice}
              strikethroughPrice={hasDiscount ? product.preDiscountedPrice : undefined}
            />
          );
        })}

        {conciergeRow && (
          <PriceRow
            label={tq`Concierge`}
            price={conciergeRow.discountedPrice ?? conciergeRow.basePrice}
            strikethroughPrice={conciergeRow.discountedPrice ? conciergeRow.preDiscountPrice : undefined}
          />
        )}
      </Flex>

      {hasAnyDiscount && promoCode && (
        <TextV2
          typographyVariant="hed1"
          fontSize={{ _: '11px', md: '15px' }}
          color="white"
          marginTop={{ _: 7, md: '38px' }}
        >
          {tq`*Use code: ${promoCode}`}
        </TextV2>
      )}
    </Flex>
  );
};
